import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ensure } from 'components/sharpr/ensure/ensure';

export function SlackPostRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.SlackPost', {
		url: '/slackPost',
		controllerAs: 'vm',
		controller: SlackPostCtrl,
		templateUrl: '/ng/hub/PushToSlack/SlackPostCtrl.html?' + window.BUILD,
		resolve: {
			hasValidToken: (Identity, Api, Logger) => {
				'ngInject';
				return Api.get('/slack/has_slack_connection')
					.then(response => {
						if (response.success === true) return true;
						else return false;
					})
					.catch(error => {
						Logger.write('PUSH_TO_SLACK_DEBUG', 'Error checking if token is valid:', error);
					});
			},
			workspaces: (Api, Logger) => {
				'ngInject';
				return Api.get('/slack/user_workspace_list')
					.then(response => {
						return ensure.array(response);
					})
					.catch(error => {
						Logger.write('PUSH_TO_SLACK_DEBUG', 'Error getting workspace list:', error);
						return [];
					});
			},
			$style: () => AssetManager.css('/ng/hub/PushToSlack/SlackPostCtrl.css'),
			$title: () => 'Send post to Slack',
		},
	});
}

export class SlackPostCtrl {
	constructor(Api, Identity, $stateParams, Logger, Cfg, modal, hasValidToken, workspaces) {
		'ngInject';
		// Save injections
		this.Api = Api;
		this.Identity = Identity;
		this.postId = $stateParams.postId;
		this.Logger = Logger;
		this.Cfg = Cfg;
		this.modal = modal;
		this.hasValidToken = hasValidToken;
		this.availableWorkspaces = workspaces;
		// Kick off initial function
		this.setup();
	}

	// This function will grab a list of workspaces, and based off the length do a few things.
	setup() {
		if (this.availableWorkspaces.length === 0) {
			this.workspaceErrorMsg =
				'Sorry, no connections for Slack have been made, please see your account admin to get this set up.';
			this.Logger.write('PUSH_TO_SLACK_DEBUG', 'Available workspaces is 0.');
			// bail
			return (this.workspaceError = true);
		}
		// If there is only 1, select automatically and pass to selectedWorkspace handler
		else if (this.availableWorkspaces.length === 1) {
			this.hideSelectWorkspace = true;
			this.selectedWorkspace = this.availableWorkspaces[0].slack_team_id;
			this.handleSelectedWorkspace();
		}
		// Otherwise the HTML component will invoke the workspace handler upon user selection.
	}

	// Determines if the selected workspace is oauth'd for that invoking user
	handleSelectedWorkspace() {
		try {
			this.oAuthWindow.close();
		} catch (e) {}
		// Turn off the other inputs, ex: if people switch between workspaces, it will hide the other
		// inputs until the channel list comes back
		this.hasSelectedChannel = false;
		this.hasChannelList = false;
		this.showMessage = false;
		this.hasChannelError = false;
		this.hasWorkspaceError = false;
		// reference the selectedWorkspace against the list of available
		let match = this.availableWorkspaces.find(workspace => {
			return workspace.slack_team_id === this.selectedWorkspace;
		});
		// If the found match obj has the property has_oauth set to true of false run these functions.
		match.has_oauth ? this.getSlackChannels(match.slack_team_id, match.slack_team_name) : this.initiateOAuth();
	}

	// This sets the selected channel
	setSelectedChannel(channelObj) {
		this.selectedChannel = channelObj.channel_id;
		this.showMessage = true;
	}

	getSlackChannels(teamId, teamName) {
		// Variable to show users we are fetching data for this team on loading indicator
		this.teamName = teamName;
		// enable trigger variables, this will now show a loading indicator
		this.loading = true;
		// this one will allow the div to be available to render.
		this.hasSelectedChannel = true;
		const userEmail = this.Identity.user().email;
		this.Api.get(`/slack/channel_list/${teamId}/${userEmail}`)
			.then(response => {
				if (response.success === false) {
					this.channelErrorMsg =
						'Sorry, we were unable to find channels for the given workspace. Please select a new workspace.';
					this.Logger.write('PUSH_TO_SLACK_DEBUG', 'Error getting channel list:', response);
					// bail
					return (this.hasChannelError = true);
				}
				this.channels = response.data;
				// Turn off loading trigger variable
				this.loading = false;
				// Let angular know we have channel data and display everything else
				this.hasChannelList = true;
			})
			.catch(error => {
				this.Logger.write('PUSH_TO_SLACK_DEBUG', 'Error getting channel list for selected workspace:', error);
			});
	}

	// Gets user email and text from the text area html and sends it to node to post in slack.
	pushToSlack() {
		const msgData = {
			email: this.Identity.user().email,
			team_id: this.selectedWorkspace,
			channel_id: this.selectedChannel,
			post_id: this.postId,
			pretext: this.pretextValue,
		};
		this.Api.post(`/slack/push_to_slack`, msgData)
			.then(response => {
				if (response.success === false) {
					this.pushToSlackErrorMsg =
						'Error sending post to Slack, please close the window and try again. If issue persists, please reach out to support@sharpr.com';
					this.Logger.write('PUSH_TO_SLACK_DEBUG', 'Error sending post to Slack:', response);
					// bail
					return (this.pushToSlackError = true);
				} else {
					this.success = true;
					// Close window after 5 seconds automatically.
					setTimeout(() => {
						this.modal.resolveByTitle('Push to Slack');
					}, 5000);
				}
			})
			.catch(error => {
				this.Logger.write('PUSH_TO_SLACK_DEBUG', 'Error sending post to Slack:', error);
			});
	}

	// Kicks off the oauth process
	async initiateOAuth() {
		this.showMessage = false;
		const { username: slackAppClientId } = await this.Api.get(`/credentials/hub_default?type=slack-app`);
		if (!slackAppClientId) {
			return this.modal.alert({
				text: 'No Slack connection data found, please have an account admin install the slack integration for this hub.',
			});
		}
		const constrain = num => Math.max(0, Math.round(num));
		const width = 490;
		const height = 775;
		const top = constrain(window.outerHeight / 2 + window.screenY - height / 2);
		const left = constrain(window.outerWidth / 2 + window.screenX - width / 2);
		const redirectUri = encodeURIComponent(`${window.BASE_URL}/slack/request_slack_token`);
		const url = `https://slack.com/oauth/authorize?scope=channels:read,groups:read,mpim:read,im:read,users:read,users:read.email,chat:write:user,chat:write:bot&client_id=${slackAppClientId}&state=SIGN_IN&redirect_uri=${redirectUri}`;
		this.oAuthWindow = window.open(url, 'Slack OAuth', `top=${top},left=${left},width=${width},height=${height}`);
		window.finishOAuth1 = evt => {
			if (evt.success !== true) {
				this.oauthErrorMsg =
					'Could not authorize, please try again. If error persists, please reach out to support@sharpr.com';
				this.Logger.write('PUSH_TO_SLACK_DEBUG', 'Error finishing Oauth', evt);
				// bail
				return (this.oauthError = true);
			}
			this.oAuthWindow.close();
			// find the matching obj and set the has_oauth property = true
			let match = this.availableWorkspaces.find(workspace => {
				return workspace.slack_team_id === this.selectedWorkspace;
			});
			match.has_oauth = true;
			this.getSlackChannels(match.slack_team_id, match.slack_team_name);
		};
	}

	cancelPush() {
		this.modal.resolveByTitle('Push to Slack');
	}
}
